<template>
    <ContentComp>
        <AlertMessage
            v-if="warningsAcknowledgedByName"
            type="success"
        >
            <div
                v-html="
                    $ti('shooting.warningsacknowledged', {
                        acknowledgedby: warningsAcknowledgedByName,
                    })
                "
            />
        </AlertMessage>
        <FormLayout>
            <FormLayoutRow>
                <FormLayoutLabel text="shooting.lane" />
                <InputNullableInt v-model="lane" />
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel text="shooting.shootingnumber" />
                <InputNullableInt v-model="shootingNumber" />
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel text="shooting.misses" />
                <InputNullableInt v-model="misses" />
            </FormLayoutRow>
        </FormLayout>
    </ContentComp>
</template>
<script setup lang="ts">
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { AlertMessage, ContentComp } from '@/components';
import { ref } from 'vue';
import { useTransactions } from '@/composeables';
import { shootingMissesToShotsArray } from '@/utilities/utils';
import { useEventCommonStore, useEventCommonStoreRef } from '@/stores';
import InputNullableInt from '@/components/Basic/InputNullableInt.vue';
import { $ti } from '@/locize';

const props = defineProps<{
    /**
     * The raceContestant the shooting is for
     */
    raceContestantId: number;

    /**
     * The contestant the shooting is for
     */
    contestantId: number;

    /**
     * The bib if the contestant the shooting is for
     */
    bib: number;

    /**
     * Which stationrole the shooting was on
     */
    stationRoleId?: number;

    /**
     * The race the shot was in
     */
    raceId?: number;

    /**
     * The starttime of the raceContestant
     */
    raceContestantStartTime: Date | null;

    /**
     * The tuid of the existing shooting if we are editing one
     */
    editingTuid: string | null;

    /**
     * If set then we default to this number in lane
     */
    prefillLane?: number;

    /**
     * If set then we default to tthis number in misses
     */
    prefillMisses?: number;

    /**
     * Name of the person who acknowledged the warnings on this shooting if any.
     *
     * Shows an AlertMessage over the boxes if set
     */
    warningsAcknowledgedByName?: string | null;
}>();

const { StationRoles } = useEventCommonStoreRef();

const misses = ref<number | null>(props.prefillMisses ?? null);
const shootingNumber = ref<number | null>(null);
const lane = ref<number | null>(props.prefillLane ?? null);

// If we have a stationRole and race, then prefill the shooting number
if (props.stationRoleId && props.raceId) {
    (async () => {
        await useEventCommonStore().EnsureLoaded(['StationRoles']);
        // The stationRoles are sorted serverside so this should always give us the correct order
        shootingNumber.value =
            // Note: There is a legacy feature where the sr.RaceID can be NULL and then be valid for all races
            // This is not supported here yet
            StationRoles.value
                .filter((sr) => sr.RaceID == props.raceId)
                .filter((sr) => sr.IsShootingStation)
                .findIndex((sr) => sr.ID === props.stationRoleId) + 1;
    })();
}

const save = async () => {
    let disablePromise = undefined;
    if (props.editingTuid !== null) {
        // TODO: This is actually not needed since the scoringengine will do it for us.
        // Remove? Or do we keep it? If its here its explicit that the old one is disabled, the scoringengine logic is hidden when you are looking at ManagerFrontend code.
        disablePromise = useTransactions().DisableTransactions([props.editingTuid]);
    }

    const addShootingPromise = useTransactions().AddShooting({
        raceContestantId: props.raceContestantId,
        contestantId: props.contestantId,
        bib: props.bib,
        lane: lane.value!,
        shootingNumber: shootingNumber.value!,
        shots: shootingMissesToShotsArray(misses.value!),
        raceContestantStartTime: props.raceContestantStartTime,
    });

    await Promise.all([disablePromise, addShootingPromise]);
};

const saveButtonEnabled = () => misses.value !== null && lane.value !== null && shootingNumber.value !== null;

defineExpose({
    save,
    saveButtonEnabled,
});
</script>
